import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { homeFor } from "../lib/home";
import type { CurrentUser } from "../lib/types";
import { Button, ErrorNote, Field, Input } from "../components/ui";
import { AuthFrame, CompactWordmark } from "../components/Brand";

const MIN_PASSWORD = 12;

interface Invitation {
  email: string;
  customerName: string;
}

/** Exactly what a first sign-in returns, because that is what runs. */
type Accepted =
  | { status: "signed_in"; user: CurrentUser }
  | { status: "totp_required"; challenge: string };

/**
 * How a customer a marketer signed up gets a login of their own.
 *
 * Public, and outside the shell: the person following this link has no account
 * yet, so anything that asks them to sign in first is a dead end.
 */
export function Invite() {
  const { token = "" } = useParams();
  const { user } = useAuth();

  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api<Invitation>(`/api/v1/invites/${token}`),
    retry: false,
  });

  if (user !== null) return <Navigate to={homeFor(user.role)} replace />;

  if (invitation.isPending) return <Frame><p className="text-sm text-dim">Loading…</p></Frame>;

  if (invitation.isError) {
    const gone = invitation.error instanceof ApiError && invitation.error.status === 404;
    return (
      <Frame>
        {/* The brand panel is a desktop thing, so on a phone this would
            otherwise be bad news from nobody in particular. */}
        <CompactWordmark />
        <h1 className="display pt-4 text-[2rem] text-ink lg:pt-0">
          {gone ? "This invitation has expired" : "Something went wrong"}
        </h1>
        <p className="mt-3 text-sm text-muted">
          {gone
            ? "It may already have been used. Ask the person who sent it for a new link."
            : "Reload the page to try again."}
        </p>
      </Frame>
    );
  }

  return <ChoosePassword token={token} invitation={invitation.data} />;
}

function ChoosePassword({ token, invitation }: { token: string; invitation: Invitation }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const accept = useMutation({
    mutationFn: () =>
      api<Accepted>(`/api/v1/invites/${token}/accept`, { method: "POST", body: { password } }),
    onSuccess: (result) => {
      // Accepting signs them in, so this lands in the portal rather than at a
      // login screen asking for the password they chose ten seconds ago.
      if (result.status !== "signed_in") return;
      setUser(result.user);
      navigate(homeFor(result.user.role), { replace: true });
    },
  });

  // Checked here as well as on the server, because typing a password twice and
  // being told at the end that they differ is a bad way to find out.
  const mismatch = confirmation.length > 0 && confirmation !== password;
  const ready = password.length >= MIN_PASSWORD && confirmation === password;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (ready) accept.mutate();
  }

  return (
    <Frame>
      <header className="space-y-2">
        <CompactWordmark />
        <h1 className="display pt-4 text-[2.5rem] text-ink lg:pt-0">Set up your account</h1>
        {/* Both the company name and the address are arbitrary length, and an
            email address has no spaces to break at. */}
        <p className="break-words text-sm text-muted">
          For {invitation.customerName}. You will sign in as{" "}
          <span className="text-ink">{invitation.email}</span>.
        </p>
      </header>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Choose a password" hint={`At least ${MIN_PASSWORD} characters.`}>
          <Input
            type="password"
            value={password}
            autoComplete="new-password"
            required
            autoFocus
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Field label="Type it again">
          <Input
            type="password"
            value={confirmation}
            autoComplete="new-password"
            required
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </Field>

        {mismatch && <ErrorNote>Those two do not match.</ErrorNote>}
        {accept.isError && <ErrorNote>{(accept.error as Error).message}</ErrorNote>}

        {/* Taller than a table button: this is the only control on the screen
            and it is pressed with a thumb. */}
        <Button
          type="submit"
          variant="primary"
          className="min-h-[2.75rem] w-full"
          disabled={!ready || accept.isPending}
        >
          {accept.isPending ? "Setting up…" : "Create account"}
        </Button>

        {/* Offered rather than imposed. Somebody accepting an invitation on a
            laptop may not have their phone to hand, and stopping them here is
            how an invitation goes unaccepted. */}
        <p className="text-xs text-dim">
          You can turn on two-factor authentication in Settings once you are in.
        </p>
      </form>
    </Frame>
  );
}

/** The same door as signing in, because this is the other side of it. */
function Frame({ children }: { children: ReactNode }) {
  return <AuthFrame>{children}</AuthFrame>;
}
